import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

export const ChangePasswordDialog = ({ trigger }) => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); setShow(false); };

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) { toast.error("New password must be at least 6 characters."); return; }
    if (next !== confirm) { toast.error("New password and confirm password don't match."); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated. Please use the new password next time you sign in.");
      setOpen(false);
      reset();
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><KeyRound className="h-4 w-4" />Change password</DialogTitle>
          <DialogDescription>Use at least 6 characters. You'll stay signed in on this device.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Current password</Label>
            <Input type={show ? "text" : "password"} required autoComplete="current-password"
              value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="change-password-current" />
          </div>
          <div className="space-y-1.5">
            <Label>New password</Label>
            <Input type={show ? "text" : "password"} required minLength={6} autoComplete="new-password"
              value={next} onChange={(e) => setNext(e.target.value)} data-testid="change-password-new" />
          </div>
          <div className="space-y-1.5">
            <Label>Confirm new password</Label>
            <Input type={show ? "text" : "password"} required minLength={6} autoComplete="new-password"
              value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="change-password-confirm" />
          </div>
          <button type="button" onClick={() => setShow((s) => !s)}
            className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5"
            data-testid="change-password-toggle-visibility">
            {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {show ? "Hide passwords" : "Show passwords"}
          </button>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="change-password-submit"
              className="bg-[#2563EB] hover:bg-[#1D4ED8] hover:text-white">
              {saving ? "Updating…" : "Update password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
